<template>
  <Wrap>
    <header class="skeleton-head">
      <SkeletonItem
        v-for="(titleClass, titleIndex) in titleLineClasses"
        :key="`skeleton-title-${titleIndex}`"
        is-animated
        :class="titleClass"
      />

      <!-- Author + Date -->
      <div class="skeleton-author-row">
        <div class="skeleton-author">
          <SkeletonItem
            is-animated
            class="skeleton-avatar"
            border-radius="50%"
          />
          <div class="skeleton-author-info">
            <SkeletonItem
              is-animated
              class="skeleton-author-name"
            />
            <SkeletonItem
              is-animated
              class="skeleton-author-desc"
            />
          </div>
        </div>
        <SkeletonItem
          is-animated
          class="skeleton-date"
        />
      </div>

      <!-- Cover image (aspect-ratio 16:9) -->
      <SkeletonItem
        is-animated
        class="skeleton-cover"
      />
    </header>

    <!-- Table of contents area + Search + Intro -->
    <div class="skeleton-toc-area">
      <!-- Search block -->
      <div class="skeleton-search">
        <SkeletonItem
          is-animated
          class="skeleton-search-title"
        />
        <div class="skeleton-search-form">
          <SkeletonItem
            v-for="(fieldClass, fieldIndex) in searchFormFieldClasses"
            :key="`skeleton-search-field-${fieldIndex}`"
            is-animated
            :class="fieldClass"
          />
        </div>
      </div>

      <div class="skeleton-intro">
        <SkeletonItem
          v-for="(introModifier, introIndex) in introLineModifiers"
          :key="`skeleton-intro-${introIndex}`"
          is-animated
          class="skeleton-intro-line"
          :class="introModifier"
        />
      </div>
    </div>
  </Wrap>

  <Wrap>
    <div class="skeleton-content post-wrap">
      <template
        v-for="(chunk, chunkIndex) in contentChunks"
        :key="`skeleton-content-${chunkIndex}`"
      >
        <SkeletonItem
          v-if="chunk.kind === 'heading'"
          is-animated
          :class="chunk.level === 'h2' ? 'skeleton-h2' : 'skeleton-h3'"
        />
        <SkeletonItem
          v-else-if="chunk.kind === 'image'"
          is-animated
          class="skeleton-image"
        />
        <SkeletonItem
          v-else
          is-animated
          class="skeleton-paragraph"
          :class="{
            'skeleton-paragraph--short': chunk.width === 'short',
            'skeleton-paragraph--medium': chunk.width === 'medium',
          }"
        />
      </template>
    </div>
  </Wrap>
</template>

<script lang="ts" setup>
  import { SkeletonItem } from '@experience/core-skeleton-lib';
  import Wrap from '@/shared/ui/Wrap.vue';

  const titleLineClasses = ['skeleton-title', 'skeleton-title skeleton-title--line2'] as const;

  const searchFormFieldClasses = ['skeleton-search-input', 'skeleton-search-input', 'skeleton-search-button'] as const;

  const introLineModifiers = ['', '', 'skeleton-intro-line--short'] as const;

  type ContentChunk =
    | { kind: 'heading'; level: 'h2' | 'h3' }
    | { kind: 'paragraph'; width?: 'short' | 'medium' }
    | { kind: 'image' };

  const contentChunks: ContentChunk[] = [
    { kind: 'heading', level: 'h2' },
    { kind: 'paragraph' },
    { kind: 'paragraph' },
    { kind: 'paragraph' },
    { kind: 'paragraph', width: 'short' },
    { kind: 'image' },
    { kind: 'paragraph' },
    { kind: 'paragraph' },
    { kind: 'paragraph', width: 'medium' },
    { kind: 'heading', level: 'h3' },
    { kind: 'paragraph' },
    { kind: 'paragraph' },
    { kind: 'paragraph', width: 'short' },
  ];
</script>

<style lang="scss" scoped>
  @import '@/app/styles/post_page';

  .skeleton-head {
    margin: 48px 0 40px;

    @include tablet {
      margin: 40px 0 32px;
    }

    @include mobile {
      margin-top: 32px;
    }
  }

  .skeleton-title {
    width: 100%;
    height: 36px;

    &--line2 {
      width: 55%;
      margin-top: 4px;
    }

    @include tablet {
      height: 32px;

      &--line2 {
        height: 32px;
      }
    }

    @include mobile {
      height: 28px;

      &--line2 {
        height: 28px;
      }
    }
  }

  .skeleton-author-row {
    display: flex;
    flex-wrap: wrap;
    gap: var(--alias-space-16, 16px);
    justify-content: space-between;
    margin-top: var(--alias-space-32, 32px);

    @include tablet {
      margin-top: var(--alias-space-24, 24px);
    }

    @include mobile {
      flex-direction: column;
    }
  }

  .skeleton-author {
    display: flex;
    gap: var(--alias-space-12, 12px);
  }

  .skeleton-avatar {
    flex-shrink: 0;
    width: 48px;
    height: 48px;
    border-radius: var(--alias-radius-rounded, 50%);
  }

  .skeleton-author-info {
    display: flex;
    flex-direction: column;
    gap: 6px;
    justify-content: center;
  }

  .skeleton-author-name {
    width: 140px;
    height: 20px;
  }

  .skeleton-author-desc {
    width: 200px;
    height: 18px;
  }

  .skeleton-date {
    align-self: flex-end;
    width: 130px;
    height: 18px;
  }

  .skeleton-cover {
    width: 100%;
    margin-top: var(--alias-space-32, 32px);
    border-radius: 2px;
    aspect-ratio: 16 / 9;

    @include tablet {
      margin-top: var(--alias-space-24, 24px);
    }

    @include mobile {
      margin-top: var(--alias-space-20, 20px);
    }
  }

  .skeleton-toc-area {
    margin-top: var(--alias-space-32, 32px);

    @include tablet {
      margin-top: var(--alias-space-24, 24px);
    }
  }

  .skeleton-search {
    margin-bottom: var(--alias-space-24, 24px);
  }

  .skeleton-search-title {
    width: 180px;
    height: 26px;
    margin-bottom: 16px;

    @include tablet {
      height: 22px;
    }
  }

  .skeleton-search-form {
    display: flex;
    gap: 12px;

    @include mobile {
      flex-direction: column;
    }
  }

  .skeleton-search-input {
    flex: 1;
    height: 48px;
    border-radius: 8px;
  }

  .skeleton-search-button {
    width: 120px;
    height: 48px;
    border-radius: 8px;

    @include mobile {
      width: 100%;
    }
  }

  .skeleton-intro {
    max-width: var(--width-content, 805px);
  }

  .skeleton-intro-line {
    width: 100%;
    height: 24px;
    margin-top: 8px;

    &--short {
      width: 50%;
    }

    @include mobile {
      height: 20px;
    }
  }

  .skeleton-content {
    max-width: var(--width-content, 805px);
  }

  .skeleton-h2 {
    width: 45%;
    height: 32px;
    margin-top: var(--alias-space-48, 48px);

    @include tablet {
      height: 24px;
      margin-top: var(--alias-space-32, 32px);
    }
  }

  .skeleton-h3 {
    width: 35%;
    height: 26px;
    margin-top: var(--alias-space-32, 32px);

    @include tablet {
      height: 20px;
      margin-top: var(--alias-space-24, 24px);
    }
  }

  .skeleton-paragraph {
    width: 100%;
    height: 20px;
    margin-top: var(--alias-space-24, 24px);

    &--short {
      width: 40%;
    }

    &--medium {
      width: 65%;
    }

    @include tablet {
      margin-top: var(--alias-space-16, 16px);
    }
  }

  .skeleton-image {
    width: 100%;
    margin: var(--alias-space-32, 32px) 0;
    border-radius: 2px;
    aspect-ratio: 16 / 10;

    @include mobile {
      margin: var(--alias-space-24, 24px) 0;
    }
  }
</style>
